class RelatedColours extends HTMLElement {
  connectedCallback () {
    const shop = window.Shopify?.shop
    const tokenMap = {
      'holland-cooper-us.myshopify.com': '4b30dc1c20ff119f17b4c3a75a433ad0',
      default: 'b03aa3be758de5476316ebef2ea64729'
    }

    this.accessToken = tokenMap[shop] || tokenMap.default
    this.apiUrl = `https://${window.location.hostname}/api/2026-01/graphql.json`

    this.usePackshotImages = this.dataset.usePackshotImages == 'true' ? true : false
    this.sortByDateTags = this.dataset.sortByDateTags === 'true'

    this.hasOverrides = this.dataset.hasOverrides == 'true' ? true : false

    this.hideSaleProducts = this.dataset.hideSaleProducts === 'true'

    this.excludedProducts = this.dataset.excludedProducts
      ?.split(',')
      .map(h => h.trim())
      .filter(Boolean)

    this.handle = this.dataset.relatedColoursHandle
    this.title = this.dataset.relatedColoursTitle
    this.defaultColour = this.extractColour(this.title)

    this.productsContainer = this.querySelector('[data-el="related-colours.products"]')
    this.titleEl = this.querySelector('[data-el="related-colours.title"]')
    this.titleContainerEl = this.querySelector('[data-el="related-colours.titleContainer"]')

    this.manualProducts = this.getManualProducts()

    if (!this.hasOverrides) {
      this.init()
    } else {
      this.setupHoverEvents()
      this.setupScrollTitle()
    }
  }

  getManualProducts () {
    if (!this.dataset.relatedProducts) return []

    try {
      const products = JSON.parse(this.dataset.relatedProducts)
      return Array.isArray(products) ? products : []
    } catch (e) {
      console.warn('[RelatedColours] Failed to parse related-products JSON:', e)
      return []
    }
  }

  extractColour (title) {
    const match = title.match(/\((.*?)\)/)
    return match ? match[1].trim() : ''
  }

  getSearchTitle (title) {
    return title.split('(')[0].trim()
  }

  async init () {
    console.log('related', this.hasOverrides)
    const baseTitle = this.getSearchTitle(this.title).replace(/'/g, "\\'")
    const searchQuery = `title:'${baseTitle}' AND published_status:published`

    const query = `
query getRelatedColourVariants($search: String!) {
products(first: 60, query: $search, sortKey: CREATED_AT, reverse: true) {
edges {
  node {
    title
    productType
    handle
    tags
    availableForSale
    onlineStoreUrl
    compareAtPriceRange {
      maxVariantPrice {
        amount
      }
    }
    priceRange {
      maxVariantPrice {
        amount
      }
    }
    metafields(identifiers: [
      { namespace: "bao", key: "related_products_position" }
      { namespace: "bao", key: "related_products_exclude" }
      { namespace: "custom", key: "gender" }
    ]) {
      key
      value
    }
    images(first: ${this.usePackshotImages ? 20 : 1}) {
      edges {
        node {
          url
          altText
        }
      }
    }
  }
}
}
}
`

    const res = await fetch(this.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.accessToken
      },
      body: JSON.stringify({ query, variables: { search: searchQuery } })
    })

    const json = await res.json()
    const fetchedProducts = json?.data?.products?.edges.map(edge => edge.node) || []
    console.log(
      '🚀 ~ related-colours.js:90 ~ RelatedColours ~ init ~ fetchedProducts:',
      fetchedProducts
    )

    // Filter out current product + exclude=true
    const currentGender = this.dataset.gender?.toLowerCase()

    const filtered = fetchedProducts.filter(p => {
      if (p.handle === this.handle) return false
      if (this.excludedProducts?.includes(p.handle)) return false
      if (p.productType?.toLowerCase() === 'sets') return false
      if (!p.onlineStoreUrl) return false

      const compareAt = Number(p.compareAtPriceRange?.maxVariantPrice?.amount || 0)
      const price = Number(p.priceRange?.maxVariantPrice?.amount || 0)
      console.log('related', p.handle, compareAt, price)
      if (this.hideSaleProducts && compareAt > price) return false

      const exclude = p.metafields?.find(mf => mf?.key === 'related_products_exclude')
      if (exclude?.value === 'true') return false

      if (currentGender) {
        const productGender = p.metafields?.find(mf => mf?.key === 'gender')?.value?.toLowerCase()
        if (productGender && productGender !== currentGender) return false
      }

      return true
    })

    const sorted = filtered.sort((a, b) => {
      const posA = parseInt(
        a.metafields.find(mf => mf?.key === 'related_products_position')?.value || 999,
        10
      )

      const posB = parseInt(
        b.metafields.find(mf => mf?.key === 'related_products_position')?.value || 999,
        10
      )

      // Position always takes priority
      if (posA !== posB) {
        return posA - posB
      }

      // If date sorting is disabled, keep existing order
      if (!this.sortByDateTags) {
        return 0
      }

      const dateA = this.getLatestDateFromTags(a.tags)
      const dateB = this.getLatestDateFromTags(b.tags)

      // Neither has a date -> preserve existing order
      if (!dateA && !dateB) return 0

      // Products with dates come before those without
      if (!dateA) return 1
      if (!dateB) return -1

      // Latest date first
      return dateB - dateA
    })

    const combined = [...this.manualProducts, ...sorted]
    this.renderProducts(combined)
  }

  getProductImageUrl (product, colour) {
    // Manual metafield product (Liquid JSON)
    if (typeof product.featured_image === 'string') {
      return {
        url: product.featured_image.startsWith('//')
          ? `https:${product.featured_image}`
          : product.featured_image,
        alt: colour
      }
    }

    // API product
    const images = product.images?.edges ?? []

    if (!images.length) {
      return { url: '', alt: colour }
    }

    const image = this.usePackshotImages
      ? images.find(({ node }) => node.altText?.includes('[packshot]'))?.node ?? images[0].node
      : images[0].node

    const url = new URL(image.url)
    url.searchParams.set('width', '300')

    return {
      url: url.toString(),
      alt: image.altText || colour
    }
  }

  renderProducts (products) {
    if (!products.length) {
      this.remove()
      return
    }

    const fragment = document.createDocumentFragment()

    products.forEach((product, index) => {
      const colour = this.extractColour(product.title)

      const { url: imageUrl, alt: imageAlt } = this.getProductImageUrl(product, colour)

      const li = document.createElement('li')
      li.className = 'prd-RelatedProducts_Product'
      li.style.order =
        product.metafields?.find(mf => mf?.key === 'related_products_position')?.value || index

      const a = document.createElement('a')
      a.href = `/products/${product.handle}`
      a.dataset.el = 'related-colours.colour'
      a.dataset.colour = colour
      a.dataset.exclude =
        product.metafields?.find(mf => mf?.key === 'related_products_exclude')?.value || 'false'

      if (imageUrl) {
        const img = document.createElement('img')
        img.src = imageUrl
        img.alt = imageAlt
        a.appendChild(img)
      }

      li.appendChild(a)
      fragment.appendChild(li)
    })

    this.productsContainer.innerHTML = ''
    this.productsContainer.appendChild(fragment)

    this.setupHoverEvents()
    this.setupScrollTitle()
  }

  getLatestDateFromTags (tags = []) {
    const dateRegex = /^\d{2}\/\d{2}\/\d{2}$/

    const dates = tags
      .filter(tag => dateRegex.test(tag))
      .map(tag => {
        const [day, month, year] = tag.split('/').map(Number)

        // Convert YY -> YYYY
        const fullYear = 2000 + year

        return new Date(fullYear, month - 1, day)
      })

    if (!dates.length) return null

    return dates.sort((a, b) => b - a)[0]
  }

  setupScrollTitle () {
    if (this.scrollTitleObserver) {
      this.scrollTitleObserver.disconnect()
      this.scrollTitleObserver = null
    }

    if (!window.BAO.mediaBreakpoints.isXsViewport) return

    const container = this.querySelector('.prd-RelatedProducts_Container')
    if (!container) return

    const colourEls = this.querySelectorAll('[data-el="related-colours.colour"]')
    if (!colourEls.length) return

    const visibleEls = new Set()

    this.scrollTitleObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            visibleEls.add(entry.target)
          } else {
            visibleEls.delete(entry.target)
          }
        })

        const activeEl = [...colourEls].find(el => visibleEls.has(el))

        if (!activeEl) return

        this.titleEl.textContent = activeEl.dataset.colour
        this.titleContainerEl.classList.add('prd-RelatedProducts_Selected-visible')

        colourEls.forEach(el => el.classList.remove('prd-RelatedProducts_Link-active'))
        activeEl.classList.add('prd-RelatedProducts_Link-active')
      },
      {
        root: container,
        rootMargin: '0px -50% 0px -20px',
        threshold: 0
      }
    )

    colourEls.forEach(el => this.scrollTitleObserver.observe(el))
  }

  setupHoverEvents () {
    if (window.BAO.mediaBreakpoints.isXsViewport) return

    const colourEls = this.querySelectorAll('[data-el="related-colours.colour"]')
    colourEls.forEach(el => {
      el.addEventListener('mouseenter', () => {
        this.title = el.dataset.colour
        this.titleEl.textContent = el.dataset.colour
        this.titleContainerEl.classList.add('prd-RelatedProducts_Selected-visible')
      })
      el.addEventListener('mouseleave', () => {
        this.title = this.defaultColour
        this.titleEl.textContent = this.defaultColour
        this.titleContainerEl.classList.remove('prd-RelatedProducts_Selected-visible')
      })
    })
  }
}

if (!customElements.get('related-colours')) {
  customElements.define('related-colours', RelatedColours)
}
